// §11.8 — the PDF reader: paged view, zoom, text search, download.
//
// Pages are rendered continuously and the page number follows what is actually under the top of
// the viewport, because that is what a reader scrolling a document expects the counter to say —
// the Previous/Next buttons scroll the same surface rather than swapping a single page in and out.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { basename, type HangarAPI, type PdfShellAccess, type VfsPath } from '@hangar/shared';
import { COPY } from '@/copy';
import { PDF_ZOOM_MAX, PDF_ZOOM_MIN, PDF_ZOOM_STEP } from '@/config';
import { PdfPageView, type PageMatch } from '@/assets/pdf/PdfPageView';
import { parsePdf, type PdfDocument } from '@/assets/pdf/document';
import { findMatches, matchNearPage } from '@/assets/pdf/search';

type State =
  | { readonly kind: 'empty' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly doc: PdfDocument }
  | { readonly kind: 'error'; readonly message: string };

export function Pdf({ api, access }: { api: HangarAPI; access: PdfShellAccess }) {
  const [path, setPath] = useState<VfsPath | null>(null);
  const [state, setState] = useState<State>({ kind: 'empty' });
  const [zoom, setZoom] = useState(1);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const scroller = useRef<HTMLDivElement | null>(null);

  // §9.5 — the path this window was launched to open (D-080).
  useEffect(() => {
    void api.launch.openedPath().then((opened) => {
      if (opened) setPath(opened);
    });
  }, [api]);

  useEffect(() => {
    if (!path) return;
    let cancelled = false;
    setState({ kind: 'loading' });
    void api.window.setTitle(basename(path));

    void api.fs
      .read(path)
      .then((bytes) => {
        if (cancelled) return;
        // Parsing is synchronous and can take a moment on a long document; the busy flag is what
        // the window chrome shows while it does (§5.1).
        void api.window.setBusy(true);
        try {
          setState({ kind: 'ready', doc: parsePdf(bytes) });
        } catch (error) {
          setState({ kind: 'error', message: error instanceof Error ? error.message : COPY.pdf.unreadable });
        } finally {
          void api.window.setBusy(false);
        }
      })
      .catch(() => {
        if (!cancelled) setState({ kind: 'error', message: COPY.pdf.unreadable });
      });

    return () => {
      cancelled = true;
    };
  }, [api, path]);

  const doc = state.kind === 'ready' ? state.doc : null;
  const matches = useMemo(() => (doc ? findMatches(doc, query) : []), [doc, query]);

  // Where the reader is, without making the page a dependency of the search: a new result set
  // should start from the current page, but *moving through* results must not reset the cursor.
  const pageRef = useRef(1);
  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  useEffect(() => {
    setActive(matches.length === 0 ? -1 : matchNearPage(matches, pageRef.current - 1));
  }, [matches]);

  const goToPage = useCallback((target: number) => {
    const node = scroller.current?.querySelector(`[data-page="${target}"]`);
    node?.scrollIntoView({ block: 'start' });
  }, []);

  // The active match owns the scroll position while searching.
  useEffect(() => {
    const match = matches[active];
    if (!match) return;
    setPage(match.page + 1);
    goToPage(match.page + 1);
  }, [active, matches, goToPage]);

  const onScroll = useCallback(() => {
    const node = scroller.current;
    if (!node) return;
    const pages = node.querySelectorAll<HTMLElement>('[data-page]');
    let current = 1;
    for (const element of pages) {
      if (element.offsetTop - node.scrollTop <= node.clientHeight / 2) {
        current = Number(element.dataset['page'] ?? 1);
      }
    }
    setPage(current);
  }, []);

  const step = useCallback(
    (delta: number) => {
      if (!doc) return;
      const target = Math.min(doc.pageCount, Math.max(1, page + delta));
      setPage(target);
      goToPage(target);
    },
    [doc, page, goToPage],
  );

  const stepMatch = useCallback(
    (delta: number) => {
      if (matches.length === 0) return;
      setActive((current) => (current + delta + matches.length) % matches.length);
    },
    [matches.length],
  );

  const matchesFor = useCallback(
    (pageIndex: number): PageMatch[] =>
      matches
        .filter((match) => match.page === pageIndex)
        .map((match) => ({
          runIndex: match.runIndex,
          start: match.start,
          length: match.length,
          ordinal: match.ordinal,
        })),
    [matches],
  );

  if (!path || state.kind === 'empty') {
    return (
      <div className="pdf is-empty" data-testid="pdf">
        <p data-testid="pdf-empty">{COPY.pdf.empty}</p>
      </div>
    );
  }

  return (
    <div className="pdf" data-testid="pdf">
      <div className="pdf-bar">
        <button
          type="button"
          data-testid="pdf-prev"
          aria-label={COPY.pdf.previousPage}
          disabled={page <= 1}
          onClick={() => step(-1)}
        >
          ‹
        </button>
        <span className="pdf-pages" data-testid="pdf-page-indicator">
          {COPY.pdf.pageLabel} {page} {COPY.pdf.of(doc?.pageCount ?? 0)}
        </span>
        <button
          type="button"
          data-testid="pdf-next"
          aria-label={COPY.pdf.nextPage}
          disabled={doc === null || page >= doc.pageCount}
          onClick={() => step(1)}
        >
          ›
        </button>

        <span className="pdf-spacer" />

        <input
          className="pdf-search"
          type="search"
          data-testid="pdf-search"
          aria-label={COPY.pdf.search}
          placeholder={COPY.pdf.searchPlaceholder}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') stepMatch(event.shiftKey ? -1 : 1);
          }}
        />
        <span className="pdf-matches" data-testid="pdf-matches" role="status">
          {query.trim().length === 0 ? '' : COPY.pdf.matches(active + 1, matches.length)}
        </span>
        <button
          type="button"
          data-testid="pdf-search-prev"
          aria-label={COPY.pdf.searchPrevious}
          disabled={matches.length === 0}
          onClick={() => stepMatch(-1)}
        >
          ⌃
        </button>
        <button
          type="button"
          data-testid="pdf-search-next"
          aria-label={COPY.pdf.searchNext}
          disabled={matches.length === 0}
          onClick={() => stepMatch(1)}
        >
          ⌄
        </button>

        <span className="pdf-spacer" />

        <button
          type="button"
          data-testid="pdf-zoom-out"
          aria-label={COPY.pdf.zoomOut}
          onClick={() => setZoom((current) => Math.max(PDF_ZOOM_MIN, current / PDF_ZOOM_STEP))}
        >
          −
        </button>
        <span className="pdf-zoom" data-testid="pdf-zoom">{`${Math.round(zoom * 100)}%`}</span>
        <button
          type="button"
          data-testid="pdf-zoom-in"
          aria-label={COPY.pdf.zoomIn}
          onClick={() => setZoom((current) => Math.min(PDF_ZOOM_MAX, current * PDF_ZOOM_STEP))}
        >
          +
        </button>
        <button
          type="button"
          data-testid="pdf-zoom-fit"
          onClick={() => {
            const width = doc?.pages[page - 1]?.width;
            const available = scroller.current?.clientWidth;
            if (!width || !available) return;
            setZoom(Math.min(PDF_ZOOM_MAX, Math.max(PDF_ZOOM_MIN, (available - 32) / width)));
          }}
        >
          {COPY.pdf.fit}
        </button>

        <button type="button" data-testid="pdf-download" onClick={() => void access.download(path)}>
          {COPY.pdf.download}
        </button>
      </div>

      {doc?.limitation ? (
        <p className="pdf-limitation" role="status" data-testid="pdf-limitation">
          {doc.limitation === 'encrypted' ? COPY.pdf.encrypted : COPY.pdf.unsupported(doc.limitation)}
        </p>
      ) : null}

      <div className="pdf-scroll" data-testid="pdf-scroll" ref={scroller} onScroll={onScroll}>
        {state.kind === 'loading' ? <p data-testid="pdf-loading">{COPY.pdf.loading}</p> : null}
        {state.kind === 'error' ? (
          <p className="pdf-error" data-testid="pdf-error">
            {COPY.pdf.unreadable}
          </p>
        ) : null}
        {doc?.pages.map((entry) => (
          <PdfPageView
            key={entry.index}
            page={entry}
            zoom={zoom}
            matches={matchesFor(entry.index)}
            activeMatch={active}
          />
        ))}
      </div>
    </div>
  );
}
